import { Sun, Wind, Droplets, Flame, RefreshCw } from "lucide-react";
import { motion } from "motion/react";
import { ImageWithFallback } from "./figma/ImageWithFallback";
import { useState } from "react";

export function EnergyBreakdown() {
  const [hoveredCell, setHoveredCell] = useState<{ day: number; hour: number } | null>(null);
  const [isHeatmapHovered, setIsHeatmapHovered] = useState(false);
  const [heatmapKey, setHeatmapKey] = useState(0);

  // Generate dynamic heatmap data for 30 days x 24 hours (green energy percentage)
  // Shows transition from coal to green energy, averaging around 70% green
  const generateHeatmapData = () => {
    const data: number[][] = [];
    for (let day = 0; day < 30; day++) {
      const dayData: number[] = [];
      const dayVariation = Math.random() * 10 - 5; // -5 to +5 variation per day
      
      for (let hour = 0; hour < 24; hour++) {
        let baseValue = 0;
        
        // Night hours (0-5, 20-23): Low green energy (more coal)
        if (hour >= 0 && hour < 6) {
          baseValue = 45 + hour * 2; // 45-55%
        } 
        // Morning ramp (6-11): Increasing green energy
        else if (hour >= 6 && hour < 12) {
          baseValue = 55 + (hour - 6) * 7; // 55-97%
        }
        // Peak solar (12-15): High green energy
        else if (hour >= 12 && hour < 16) {
          baseValue = 93 - (hour - 12) * 1; // 93-90%
        }
        // Evening decline (16-20): Decreasing green energy
        else if (hour >= 16 && hour < 20) {
          baseValue = 90 - (hour - 16) * 5; // 90-70%
        }
        // Late evening (20-23): Low green energy
        else {
          baseValue = 70 - (hour - 20) * 7; // 70-49%
        }
        
        // Add random variation and day variation
        const randomVariation = Math.random() * 8 - 4; // -4 to +4
        let finalValue = Math.round(baseValue + dayVariation + randomVariation);
        
        // Occasionally add null for "no data" (1% chance)
        if (Math.random() < 0.01) {
          dayData.push(null as any);
        } else {
          // Clamp between 0 and 100
          finalValue = Math.max(0, Math.min(100, finalValue));
          dayData.push(finalValue);
        }
      }
      data.push(dayData);
    }
    return data;
  };

  const [heatmapData, setHeatmapData] = useState(generateHeatmapData());

  // Regenerate data on hover for demo
  const handleHeatmapHover = () => {
    setIsHeatmapHovered(true);
  };

  const handleHeatmapLeave = () => {
    setIsHeatmapHovered(false);
  };

  const handleRefreshData = () => {
    setHeatmapData(generateHeatmapData());
    setHeatmapKey(prev => prev + 1);
  };

  // Function to get color based on green energy percentage
  // White (no reading) -> Dark Grey (coal) -> Light Grey -> Light Emerald -> Dark Emerald (green)
  const getColor = (percentage: number | null) => {
    if (percentage === null || percentage === undefined) return "bg-white border border-gray-200"; // No meter reading
    if (percentage >= 90) return "bg-emerald-600"; // High green energy
    if (percentage >= 80) return "bg-emerald-500";
    if (percentage >= 70) return "bg-emerald-400";
    if (percentage >= 60) return "bg-emerald-300";
    if (percentage >= 50) return "bg-emerald-200";
    if (percentage >= 40) return "bg-emerald-100"; // Light emerald transition
    if (percentage >= 20) return "bg-gray-300"; // Light grey
    return "bg-gray-800"; // Dark grey (coal)
  };

  const energySources = [
    { 
      name: "Solar", 
      icon: Sun, 
      percentage: 40, 
      color: "text-yellow-500", 
      bgColor: "bg-yellow-50",
      image: "https://images.unsplash.com/photo-1508514177221-188b1cf16e9d?crop=entropy&cs=tinysrgb&fit=max&fm=jpg&ixid=M3w3Nzg4Nzd8MHwxfHNlYXJjaHwxfHxzb2xhciUyMHBhbmVsJTIwZmFybXxlbnwxfHx8fDE3NjQ0MTM4MDR8MA&ixlib=rb-4.1.0&q=80&w=1080&utm_source=figma&utm_medium=referral"
    },
    { 
      name: "Wind", 
      icon: Wind, 
      percentage: 20, 
      color: "text-cyan-500", 
      bgColor: "bg-cyan-50",
      image: "https://images.unsplash.com/photo-1566933971571-e3cb6990e9f8?crop=entropy&cs=tinysrgb&fit=max&fm=jpg&ixid=M3w3Nzg4Nzd8MHwxfHNlYXJjaHwxfHx3aW5kJTIwdHVyYmluZSUyMGZpZWxkfGVufDF8fHx8MTc2NDMyMTQxMXww&ixlib=rb-4.1.0&q=80&w=1080&utm_source=figma&utm_medium=referral"
    },
    { 
      name: "Hydro", 
      icon: Droplets, 
      percentage: 5, 
      color: "text-blue-500", 
      bgColor: "bg-blue-50",
      image: "https://images.unsplash.com/photo-1733001523587-ad1bc6d025c1?crop=entropy&cs=tinysrgb&fit=max&fm=jpg&ixid=M3w3Nzg4Nzd8MHwxfHNlYXJjaHwxfHxoeWRyb2VsZWN0cmljJTIwZGFtJTIwcG93ZXJ8ZW58MXx8fHwxNzY0NDEzODA0fDA&ixlib=rb-4.1.0&q=80&w=1080&utm_source=figma&utm_medium=referral"
    },
    { 
      name: "Biomass", 
      icon: Flame, 
      percentage: 5, 
      color: "text-green-500", 
      bgColor: "bg-green-50",
      image: "https://images.unsplash.com/photo-1747345341772-7516f6b54958?crop=entropy&cs=tinysrgb&fit=max&fm=jpg&ixid=M3w3Nzg4Nzd8MHwxfHNlYXJjaHwxfHxiaW9tYXNzJTIwZW5lcmd5JTIwcGxhbnR8ZW58MXx8fHwxNzY0NDEzODA1fDA&ixlib=rb-4.1.0&q=80&w=1080&utm_source=figma&utm_medium=referral"
    },
  ];

  const cleanEnergyPercentage = 70;

  return (
    <section id="energy-breakdown" className="py-16 md:py-24 bg-gradient-to-br from-gray-50 to-emerald-50">
      <div className="container mx-auto px-4">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-12"
        >
          <h2 className="text-3xl md:text-4xl lg:text-5xl mb-4 bg-gradient-to-r from-emerald-600 to-cyan-600 bg-clip-text text-transparent">
            24/7 Green Energy Coverage
          </h2>
          <p className="text-lg text-gray-600 max-w-2xl mx-auto">
            Track green energy availability across every hour of every day
          </p>
        </motion.div>

        <div className="max-w-7xl mx-auto grid lg:grid-cols-2 gap-8">
          {/* Heatmap */}
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="bg-white p-6 md:p-8 rounded-2xl shadow-lg h-full flex flex-col relative"
            onMouseEnter={handleHeatmapHover}
            onMouseLeave={handleHeatmapLeave}
          >
            <button
              onClick={handleRefreshData}
              className="absolute top-4 right-4 bg-emerald-500 hover:bg-emerald-600 text-white text-xs px-3 py-1.5 rounded-full shadow-lg z-10 flex items-center gap-1.5 transition-colors"
            >
              <RefreshCw className="w-3 h-3" />
              Demo: Data Updated
            </button>
            <div className="flex items-center justify-between mb-6 flex-wrap gap-4">
              <h3 className="text-xl text-gray-900">Monthly Energy Heatmap</h3>
              <div className="flex flex-col gap-2 text-xs">
                <div className="flex items-center gap-2">
                  <span className="text-gray-500 w-10">Coal</span>
                  <div className="flex gap-1">
                    <div className="flex flex-col items-center">
                      <div className="w-4 h-4 bg-gray-800 rounded mb-1"></div>
                      <span className="text-gray-400">0-20</span>
                    </div>
                    <div className="flex flex-col items-center">
                      <div className="w-4 h-4 bg-gray-300 rounded mb-1"></div>
                      <span className="text-gray-400">20-40</span>
                    </div>
                    <div className="flex flex-col items-center">
                      <div className="w-4 h-4 bg-emerald-100 rounded mb-1"></div>
                      <span className="text-gray-400">40-50</span>
                    </div>
                    <div className="flex flex-col items-center">
                      <div className="w-4 h-4 bg-emerald-200 rounded mb-1"></div>
                      <span className="text-gray-400">50-60</span>
                    </div>
                    <div className="flex flex-col items-center">
                      <div className="w-4 h-4 bg-emerald-300 rounded mb-1"></div>
                      <span className="text-gray-400">60-70</span>
                    </div>
                    <div className="flex flex-col items-center">
                      <div className="w-4 h-4 bg-emerald-400 rounded mb-1"></div>
                      <span className="text-gray-400">70-80</span>
                    </div>
                    <div className="flex flex-col items-center">
                      <div className="w-4 h-4 bg-emerald-500 rounded mb-1"></div>
                      <span className="text-gray-400">80-90</span>
                    </div>
                    <div className="flex flex-col items-center">
                      <div className="w-4 h-4 bg-emerald-600 rounded mb-1"></div>
                      <span className="text-gray-400">90+</span>
                    </div>
                  </div>
                  <span className="text-gray-500">Green</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-4 h-4 bg-white border border-gray-300 rounded" title="No reading"></div>
                  <span className="text-gray-500">No Data / Loss of Communication</span>
                </div>
              </div>
            </div>

            <div className="overflow-x-auto flex-1">
              <div className="inline-block min-w-full">
                {/* Hour labels */}
                <div className="flex mb-1">
                  <div className="w-8 flex-shrink-0"></div>
                  <div className="flex gap-0.5">
                    {Array.from({ length: 24 }, (_, i) => (
                      <div key={i} className="w-4 text-xs text-gray-500 text-center">
                        {i % 6 === 0 ? i : ""}
                      </div>
                    ))}
                  </div>
                </div>

                {/* Heatmap grid */}
                {heatmapData.map((dayData, dayIndex) => (
                  <div key={dayIndex} className="flex mb-0.5">
                    {/* Day label */}
                    <div className="w-8 flex-shrink-0 text-xs text-gray-500 pr-2 text-right flex items-center justify-end">
                      {(dayIndex + 1) % 5 === 0 ? dayIndex + 1 : ""}
                    </div>
                    {/* Hour cells */}
                    <div className="flex gap-0.5">
                      {dayData.map((percentage, hourIndex) => (
                        <div
                          key={hourIndex}
                          className={`w-4 h-4 ${getColor(percentage)} rounded-sm cursor-pointer hover:ring-2 hover:ring-cyan-400 transition-all relative group`}
                          onMouseEnter={() => setHoveredCell({ day: dayIndex + 1, hour: hourIndex })}
                          onMouseLeave={() => setHoveredCell(null)}
                          title={`Day ${dayIndex + 1}, Hour ${hourIndex}: ${percentage}% Green`}
                        >
                          {hoveredCell?.day === dayIndex + 1 && hoveredCell?.hour === hourIndex && (
                            <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-2 py-1 bg-gray-900 text-white text-xs rounded whitespace-nowrap z-10 pointer-events-none">
                              Day {dayIndex + 1}, {hourIndex}:00 - {percentage}%
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                ))}

                {/* Bottom axis label */}
                <div className="flex mt-2">
                  <div className="w-8 flex-shrink-0"></div>
                  <div className="text-xs text-gray-500 text-center flex-1">
                    Hours (0-23)
                  </div>
                </div>
              </div>
            </div>

            <div className="mt-4 text-sm text-gray-500">
              <p>30 days × 24 hours showing green energy availability percentage</p>
            </div>
          </motion.div>

          {/* Stats */}
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="h-full flex flex-col space-y-6"
          >
            {/* Clean Energy Percentage */}
            <div className="bg-gradient-to-br from-emerald-500 to-cyan-500 p-8 rounded-2xl text-white shadow-lg">
              <p className="text-sm opacity-90 mb-2">Average Clean Energy</p>
              <p className="text-5xl mb-2">{cleanEnergyPercentage}%</p>
              <p className="text-sm opacity-90">24/7 monthly average</p>
            </div>

            {/* Energy Sources */}
            <div className="bg-white p-6 rounded-2xl shadow-lg space-y-4 flex-1">
              {energySources.map((source, index) => (
                <div key={index} className="group cursor-pointer">
                  <div className="flex items-center gap-4 mb-2">
                    <div className="relative w-16 h-16 rounded-lg overflow-hidden flex-shrink-0">
                      <ImageWithFallback
                        src={source.image}
                        alt={source.name}
                        className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-300"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-black/40 to-transparent"></div>
                      <div className={`absolute top-1 right-1 w-6 h-6 ${source.bgColor} rounded-full flex items-center justify-center`}>
                        <source.icon className={`w-3 h-3 ${source.color}`} />
                      </div>
                    </div>
                    <div className="flex-1">
                      <div className="flex justify-between items-center mb-1">
                        <span className="text-sm text-gray-900">{source.name}</span>
                        <span className="text-sm">{source.percentage}%</span>
                      </div>
                      <div className="w-full bg-gray-200 rounded-full h-2">
                        <div 
                          className={`h-2 rounded-full`}
                          style={{ width: `${source.percentage}%`,backgroundColor:"#10b981" }}
                        ></div>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
